/*
 * FICHIER: NavigationContext.js
 * BUT: Contexte React pour gérer l'état de navigation globale
 * 
 * Le Context API de React permet de partager des données entre composants
 * sans avoir à passer des props à travers toute la hiérarchie (prop drilling).
 * 
 * Ce contexte gère :
 * - La section active actuellement affichée
 * - La possibilité de changer de section depuis n'importe quel composant
 * 
 * UTILISATION :
 * 1. Wrapper l'app avec NavigationProvider (déjà fait dans App.js)
 * 2. Dans les composants : const { activeSection, setActiveSection } = useNavigation();
 * 
 * AVANTAGES DU CONTEXT vs PROPS :
 * - Évite le prop drilling (passer des props sur plusieurs niveaux)
 * - Centralise la logique de navigation
 * - Tous les composants peuvent accéder/modifier l'état facilement
 */

// Import des outils React pour créer un contexte
import React, { createContext, useContext, useState } from 'react';

// CRÉATION DU CONTEXTE
// createContext() crée un objet contexte que les composants peuvent utiliser
const NavigationContext = createContext();

/**
 * Provider Component - Fournit le contexte à tous ses enfants
 * 
 * Ce composant :
 * 1. Maintient l'état de navigation (activeSection)
 * 2. Fournit les méthodes pour modifier cet état
 * 3. Rend cet état accessible à tous les composants enfants
 * 
 * @param {Object} props - Props du composant
 * @param {ReactNode} props.children - Composants enfants qui auront accès au contexte
 */
export const NavigationProvider = ({ children }) => {
  // ÉTAT LOCAL DU PROVIDER
  // Cet état sera partagé avec tous les composants enfants
  const [activeSection, setActiveSection] = useState('accueil');

  // VALEUR DU CONTEXTE
  // Cette objet sera accessible via useNavigation() dans tous les composants enfants
  const contextValue = {
    activeSection,      // Section actuellement active (string)
    setActiveSection    // Fonction pour changer la section active
  };

  return (
    // PROVIDER - Rend la valeur accessible aux composants enfants
    <NavigationContext.Provider value={contextValue}>
      {children} {/* Tous les composants enfants auront accès au contexte */}
    </NavigationContext.Provider>
  );
};

/**
 * Hook personnalisé pour utiliser le contexte de navigation
 * 
 * Ce hook :
 * 1. Récupère le contexte de navigation
 * 2. Vérifie qu'il est utilisé dans le bon provider
 * 3. Retourne les valeurs du contexte
 * 
 * UTILISATION DANS UN COMPOSANT :
 * const { activeSection, setActiveSection } = useNavigation();
 * 
 * GESTION D'ERREUR :
 * Si le hook est utilisé hors d'un NavigationProvider, une erreur est levée
 * 
 * @returns {Object} - Objet contenant activeSection et setActiveSection
 * @throws {Error} - Si utilisé hors d'un NavigationProvider
 */
export const useNavigation = () => {
  // RÉCUPÉRATION DU CONTEXTE
  const context = useContext(NavigationContext);
  
  // VÉRIFICATION DE SÉCURITÉ
  // Si le composant n'est pas dans un NavigationProvider, context sera undefined
  if (!context) {
    throw new Error(
      'useNavigation must be used within a NavigationProvider. ' +
      'Assurez-vous que votre composant est enveloppé dans un <NavigationProvider>.'
    );
  }
  
  // RETOUR DES VALEURS DU CONTEXTE
  return context;
};

// Export par défaut du contexte (généralement pas utilisé directement)
export default NavigationContext; 